import { Icon, IconOptions, MarkerOptions, PointExpression } from 'leaflet';
import blackArrow from '@cas/assets/images/black-arrow.svg';
import blueArrow from '@cas/assets/images/blue-arrow.svg';
import redArrow from '@cas/assets/images/red-arrow.svg';
import greenArrow from '@cas/assets/images/green-arrow.svg';
import yellowArrow from '@cas/assets/images/yellow-arrow.svg';
import grayArrow from '@cas/assets/images/gray-arrow.svg';
import blackCircle from '@cas/assets/images/black-circle.svg';
import blueCircle from '@cas/assets/images/blue-circle.svg';
import redCircle from '@cas/assets/images/red-circle.svg';
import greenCircle from '@cas/assets/images/green-circle.svg';
import yellowCircle from '@cas/assets/images/yellow-circle.svg';
import grayCircle from '@cas/assets/images/gray-circle.svg';
import iconDozerImage from '@cas/assets/images/dozer.png';
import iconLvImage from '@cas/assets/images/lv_white.png';

const ARROW_ICON_ANCHOR: PointExpression = [18, 37];

interface MarkerRotationOptions extends Pick<MarkerOptions, 'rotationAngle' | 'rotationOrigin'> {}

interface RotatableIconOptions extends Partial<IconOptions>, MarkerRotationOptions {
  iconUrl: IconOptions['iconUrl'];
  iconSize: IconOptions['iconSize'];
  iconAnchor: IconOptions['iconAnchor'];
}

const defaultRotationOptions: MarkerRotationOptions = {
  rotationAngle: 0,
  rotationOrigin: 'center center',
};

const getIconArrowOptions = (iconUrl: string): RotatableIconOptions => ({
  ...defaultRotationOptions,
  iconUrl,
  iconSize: [36, 57], // The precise icon file dimensions.
  iconAnchor: ARROW_ICON_ANCHOR, // The xy offset of the center of the small circle inside the arrow.
});

const getIconCircleOptions = (iconUrl: string): RotatableIconOptions => ({
  ...defaultRotationOptions,
  iconUrl,
  iconSize: [36, 36], // The precise icon file dimensions.
  iconAnchor: [18, 18], // The xy offset of the center of the small circle.
});

/**
 * Fixme: Several code smells. This function probably shouldn't exist at all.
 * - Parsing an SVG string to extract dimensions is ineffective and not deterministic.
 * - SVGs can be rendered without needing to know the dimensions. Consider if RotatedMarker is fit for purpose in this context.
 * - Width, height, and viewBox serve different purposes.
 */
export function getSvgDimensions(svgString: string): { width: number; height: number } | undefined {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return undefined;

    // Try width/height attributes first
    let width = svg.getAttribute('width');
    let height = svg.getAttribute('height');

    // Fallback to viewBox if width/height are not set
    if (!width || !height) {
      const viewBox = svg.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(' ');
        if (parts.length === 4) {
          width = parts[2];
          height = parts[3];
        }
      }
    }

    if (width && height) {
      return { width: Number(width), height: Number(height) };
    }
  } catch {
    console.log('Error fetching dimensions from SVG');
  }
  return undefined;
}

const getIconObjectTypeOptions = (
  svgIcon: string,
  width: number,
  height: number,
  isSvgObjectTypeIcon: boolean,
): RotatableIconOptions => {
  const iconSize: [number, number] = [width, height];
  const iconAnchor: [number, number] = isSvgObjectTypeIcon
    ? [iconSize[0] / 2, iconSize[1] / 2]
    : ARROW_ICON_ANCHOR;
  return {
    ...defaultRotationOptions,
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgIcon),
    iconSize,
    iconAnchor,
  };
};

// Arrow icons
export const IconArrowBlack = Icon.extend({ options: getIconArrowOptions(blackArrow) });
export const IconArrowBlue = Icon.extend({ options: getIconArrowOptions(blueArrow) });
export const IconArrowRed = Icon.extend({ options: getIconArrowOptions(redArrow) });
export const IconArrowGreen = Icon.extend({ options: getIconArrowOptions(greenArrow) });
export const IconArrowYellow = Icon.extend({ options: getIconArrowOptions(yellowArrow) });
export const IconArrowGray = Icon.extend({ options: getIconArrowOptions(grayArrow) });

// Circle icons
export const IconCircleBlack = Icon.extend({ options: getIconCircleOptions(blackCircle) });
export const IconCircleBlue = Icon.extend({ options: getIconCircleOptions(blueCircle) });
export const IconCircleRed = Icon.extend({ options: getIconCircleOptions(redCircle) });
export const IconCircleGreen = Icon.extend({ options: getIconCircleOptions(greenCircle) });
export const IconCircleYellow = Icon.extend({ options: getIconCircleOptions(yellowCircle) });
export const IconCircleGray = Icon.extend({ options: getIconCircleOptions(grayCircle) });

export enum IconColor {
  Black = 'black',
  Blue = 'blue',
  Red = 'red',
  Green = 'green',
  Yellow = 'yellow',
  Gray = 'gray',
}

export const getObjectIcon = (color: IconColor, heading: number | null): Icon => {
  const hasHeading = heading !== null;

  switch (color) {
    case IconColor.Black:
      return hasHeading ? new IconArrowBlack() : new IconCircleBlack();
    case IconColor.Blue:
      return hasHeading ? new IconArrowBlue() : new IconCircleBlue();
    case IconColor.Red:
      return hasHeading ? new IconArrowRed() : new IconCircleRed();
    case IconColor.Green:
      return hasHeading ? new IconArrowGreen() : new IconCircleGreen();
    case IconColor.Yellow:
      return hasHeading ? new IconArrowYellow() : new IconCircleYellow();
    case IconColor.Gray:
    default:
      return hasHeading ? new IconArrowGray() : new IconCircleGray();
  }
};

export const getObjectTypeIcon = (
  svgIcon: string,
  width: number,
  height: number,
  isSvgObjectTypeIcon: boolean,
): Icon => {
  return new Icon(getIconObjectTypeOptions(svgIcon, width, height, isSvgObjectTypeIcon));
};

export const IconDozer = Icon.extend({
  options: {
    ...defaultRotationOptions,
    iconUrl: iconDozerImage,
    iconSize: [40, 75],
    iconAnchor: [20, 37],
  },
});

export const IconLvWhite = Icon.extend({
  options: {
    ...defaultRotationOptions,
    iconUrl: iconLvImage,
    iconSize: [30, 71],
    iconAnchor: [15, 36],
  },
});
